import React from "react";
import {
  XCircleIcon,
  RefreshIcon,
  ArrowLeftIcon,
  HomeIcon,
} from "../components/icons";
import { useNavigate } from "react-router-dom";

// Error logging service — console only (no backend endpoint exists for this)
const errorLogger = {
  log: (error, errorInfo, level = "error", context = {}) => {
    const errorData = {
      message: error.toString(),
      stack: error.stack,
      componentStack: errorInfo?.componentStack,
      level,
      context,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    // Always log errors to console for debugging
    console.error("[ErrorBoundary] Caught error:", errorData.message);
    console.error("[ErrorBoundary] Component stack:", errorData.componentStack);
    console.error("[ErrorBoundary] Full stack:", errorData.stack);
    console.error("[ErrorBoundary] URL:", errorData.url);
    console.error("[ErrorBoundary] Context:", errorData.context);
  },
};

// Base Error Boundary Class
class BaseErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const { level = "error", context = {}, onError } = this.props;
    this.setState({ errorInfo });

    // Log error with context
    errorLogger.log(error, errorInfo, level, {
      ...context,
      componentName: this.props.name || "Unknown",
      retryCount: this.state.retryCount,
    });

    if (onError) {
      onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
    }));
  };

  render() {
    if (this.state.hasError) {
      const { fallback: FallbackComponent } = this.props;

      if (FallbackComponent) {
        return (
          <FallbackComponent
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            onRetry={this.handleRetry}
            retryCount={this.state.retryCount}
          />
        );
      }

      // Default fallback UI
      return (
        <DefaultErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

// Default Error Fallback Component
function DefaultErrorFallback({ error, onRetry }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface px-4">
      <div className="max-w-md w-full bg-surface-container border border-outline-variant/30 rounded-lg shadow-sm p-8 text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-error/15 mb-4">
          <XCircleIcon className="h-6 w-6 text-error" />
        </div>
        <h1 className="text-xl font-semibold text-on-surface mb-2">
          Something went wrong
        </h1>
        <p className="text-on-surface-variant mb-6 text-sm">
          We encountered an unexpected error. Please try refreshing the page or
          contact support if the problem persists.
        </p>
        <div className="space-y-3">
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-primary-container hover:bg-primary-container/80 text-on-primary font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <RefreshIcon className="h-4 w-4" />
            Refresh Page
          </button>
          <button
            onClick={onRetry}
            className="w-full bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-medium py-2.5 px-4 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
        {import.meta.env.MODE === "development" && error && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm text-on-surface-variant hover:text-on-surface">
              Show Error Details (Development)
            </summary>
            <pre className="mt-2 text-xs text-error bg-error/10 p-3 rounded-lg overflow-auto max-h-40">
              {error.toString()}
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

// Component-Level Error Fallback
function ComponentErrorFallback({ error, onRetry, componentName }) {
  return (
    <div className="p-6 bg-error/10 border border-error/30 rounded-lg">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <XCircleIcon className="h-5 w-5 text-error" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-error">
            {componentName || "Component"} Error
          </h3>
          <p className="mt-1 text-sm text-on-surface-variant">
            This component encountered an error and couldn't be displayed.
          </p>
          <div className="mt-3">
            <button
              onClick={onRetry}
              className="text-sm font-medium text-primary hover:text-primary/80"
            >
              Try loading again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Section-Level Error Fallback
function SectionErrorFallback({ error, onRetry, sectionName }) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center p-12 bg-surface-container rounded-lg border border-outline-variant/30">
      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-error/15 mb-6">
        <XCircleIcon className="h-8 w-8 text-error" />
      </div>
      <h2 className="text-lg font-semibold text-on-surface mb-2">
        {sectionName || "Section"} unavailable
      </h2>
      <p className="text-on-surface-variant mb-6 text-center max-w-sm text-sm">
        We're having trouble loading this section. Please try again or navigate
        to a different page.
      </p>
      <div className="flex gap-3">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-4 py-2 border border-outline-variant/30 rounded-lg text-sm font-medium text-on-surface bg-surface-container-high hover:bg-surface-container-highest transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Go Back
        </button>
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-on-primary bg-primary-container hover:bg-primary-container/80 transition-colors"
        >
          <RefreshIcon className="h-4 w-4" />
          Retry
        </button>
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 px-4 py-2 border border-outline-variant/30 rounded-lg text-sm font-medium text-on-surface bg-surface-container-high hover:bg-surface-container-highest transition-colors"
        >
          <HomeIcon className="h-4 w-4" />
          Home
        </button>
      </div>
    </div>
  );
}

// Network Error Fallback
function NetworkErrorFallback({ error, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center p-8">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-warning/15 mb-4">
        <svg
          className="h-6 w-6 text-warning"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
          />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-on-surface mb-2">
        Connection Problem
      </h3>
      <p className="text-on-surface-variant mb-4 text-center text-sm">
        Please check your internet connection and try again.
      </p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-on-primary bg-primary-container hover:bg-primary-container/80 transition-colors"
      >
        <RefreshIcon className="h-4 w-4" />
        Retry
      </button>
    </div>
  );
}

// Export different error boundary types
export const ErrorBoundary = BaseErrorBoundary;

export const AppErrorBoundary = (props) => (
  <BaseErrorBoundary
    {...props}
    name="App"
    level="critical"
    fallback={DefaultErrorFallback}
  />
);

export const PageErrorBoundary = (props) => (
  <BaseErrorBoundary
    {...props}
    name={props.pageName || "Page"}
    level="error"
    fallback={(fallbackProps) => (
      <SectionErrorFallback {...fallbackProps} sectionName={props.pageName} />
    )}
  />
);

export const ComponentErrorBoundary = (props) => (
  <BaseErrorBoundary
    {...props}
    name={props.componentName || "Component"}
    level="warning"
    fallback={(fallbackProps) => (
      <ComponentErrorFallback
        {...fallbackProps}
        componentName={props.componentName}
      />
    )}
  />
);

export const NetworkErrorBoundary = (props) => (
  <BaseErrorBoundary
    {...props}
    name="Network"
    level="error"
    fallback={NetworkErrorFallback}
  />
);

export default ErrorBoundary;
